import React, { useEffect, useState, useMemo } from 'react';
import {
  Card,
  Row,
  Col,
  Spin,
  Skeleton,
  Button,
  Tag,
  Tabs,
  DatePicker,
  Select,
  Typography,
  Divider,
  Empty
} from 'antd';
import ContainerBody from '@components/ContainerBody';
import Breadcrumb from '@components/Breadcrumb';
import { useNavigate, useParams } from 'react-router-dom';
import { apiGetDeviceById, apiGetAllWarnings } from '@services/ManageDevices/DeviceServices';
import {
  apiGetStatisticsCustomerByDayV2,
  apiGetStatisticsDataByMonthByCustomerV2,
  apiGetStatisticsDataByMonthV3,
  apiGetConsumptionMonitoring
} from '@services/ManageCustomers/CustomerServices';
import './styles.css';
import moment from 'moment';
import dayjs from 'dayjs';
import { convertPN, formatCurrency } from '@utils/utils';
import {
  ArrowLeftOutlined,
  WifiOutlined,
  ThunderboltOutlined,
  WarningOutlined,
  DashboardOutlined,
  UserOutlined,
  ToolOutlined,
  AlertOutlined,
  SignalFilled,
  ApiOutlined
} from '@ant-design/icons';
import Chart from '@components/charts/Chart';
import { COLORS } from '@components/charts/chart.constant';
import { isNumber } from 'lodash';
import Loading from '@components/Loading';

const { TabPane } = Tabs;
const { Option } = Select;

// ─── Helper component ─────────────────────────────────────────────────────────
const InfoRow = ({ label, value }) => (
  <div className="detail-info-row">
    <span className="detail-info-label">{label}</span>
    <span className="detail-info-value">{value ?? '—'}</span>
  </div>
);

const WarningBadge = ({ active }) =>
  active ? (
    <Tag color="orange" style={{ borderRadius: 12, fontWeight: 600 }}>
      Có
    </Tag>
  ) : (
    <Tag color="cyan" style={{ borderRadius: 12, fontWeight: 600 }}>
      Không
    </Tag>
  );

const SectionCard = ({ icon, title, children, color = '#0284c7' }) => (
  <Card
    className="detail-section-card"
    title={
      <span style={{ color, display: 'flex', alignItems: 'center', gap: 8 }}>
        {icon}
        {title}
      </span>
    }
    headStyle={{ background: '#f8faff', borderBottom: '2px solid #e6edf8' }}
    bodyStyle={{ padding: '12px 16px' }}>
    {children}
  </Card>
);

// ─── Main Component ────────────────────────────────────────────────────────────
const DeviceInformationDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();

  // ── Device data state ──
  const [deviceData, setDeviceData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // ── Yesterday's Data for Quick Stat ──
  const [yesterdayConsumption, setYesterdayConsumption] = useState(null);

  // ── Chart: By Day ──
  const [selectedDate, setSelectedDate] = useState(dayjs().subtract(1, 'day'));
  const [dayData, setDayData] = useState(null);
  const [loadingDay, setLoadingDay] = useState(false);

  // ── Warnings from API ──
  const [apiWarnings, setApiWarnings] = useState([]);
  const [loadingWarnings, setLoadingWarnings] = useState(false);
  const [selectedMonth, setSelectedMonth] = useState(dayjs().format('YYYY-MM'));
  const [monthData, setMonthData] = useState([]);
  const [loadingMonth, setLoadingMonth] = useState(false);

  const [prevMonthsConsumption, setPrevMonthsConsumption] = useState([]);
  const [loadingPrevMonths, setLoadingPrevMonths] = useState(false);

  const [currentMonthConsumption, setCurrentMonthConsumption] = useState(null);

  // ── Fetch device ──
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await apiGetDeviceById(id);
        setDeviceData(response.data?.data);
      } catch (err) {
        setError('Không thể tải dữ liệu thiết bị. Vui lòng thử lại.');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  // ── Fetch chart by day ──
  const fetchDayChart = async (date, customerId) => {
    if (!customerId) return;
    try {
      setLoadingDay(true);
      const dateStr = date.format('YYYY-MM-DD');
      const res = await apiGetStatisticsCustomerByDayV2({
        page: 1,
        size: 10,
        customerId: customerId,
        fromDate: dateStr,
        toDate: dateStr
      });
      const record = res.data?.data?.[0] || null;
      setDayData(record);
    } catch (e) {
      console.error(e);
      setDayData(null);
    } finally {
      setLoadingDay(false);
    }
  };

  // ── Fetch chart by month ──
  const fetchMonthChart = async (month, customerId) => {
    if (!customerId) return;
    try {
      setLoadingMonth(true);
      const fromDate = moment(month + '-01');
      const res = await apiGetStatisticsDataByMonthByCustomerV2({
        customer_id: customerId,
        period: fromDate.format('YYYY-MM-DD')
      });
      setMonthData(res?.data?.data || []);
    } catch (e) {
      console.error(e);
      setMonthData([]);
    } finally {
      setLoadingMonth(false);
    }
  };

  const getCommunityFromBranch = (branch) => {
    if (!branch) return 'HP';
    if (branch.includes('Hải Phòng')) return 'HP';
    if (branch.includes('Hà Nội')) return 'HN';
    return 'HP';
  };

  const fetchPrevMonthsConsumption = async (customer) => {
    if (!customer?.code && !customer?.id) return;
    try {
      setLoadingPrevMonths(true);
      const keyword = customer?.code || customer?.id;
      const community = getCommunityFromBranch(customer?.branch);
      const now = dayjs();
      const prevMonth1 = now.subtract(1, 'month');
      const prevMonth2 = now.subtract(2, 'month');

      const [res1, res2] = await Promise.all([
        apiGetStatisticsDataByMonthV3({
          page: 1,
          size: 10,
          keyWord: keyword,
          community,
          period: prevMonth1.startOf('month').format('YYYY-MM-DD'),
          dataStatus: 'with_data'
        }),
        apiGetStatisticsDataByMonthV3({
          page: 1,
          size: 10,
          keyWord: keyword,
          community,
          period: prevMonth2.startOf('month').format('YYYY-MM-DD'),
          dataStatus: 'with_data'
        })
      ]);

      setPrevMonthsConsumption([
        {
          month: prevMonth1,
          finalConsumption: res1?.data?.data?.[0]?.final_consumption
        },
        {
          month: prevMonth2,
          finalConsumption: res2?.data?.data?.[0]?.final_consumption
        }
      ]);
    } catch (e) {
      console.error(e);
    } finally {
      setLoadingPrevMonths(false);
    }
  };

  const fetchCurrentMonthConsumption = async (customer) => {
    if (!customer?.code && !customer?.id) return;
    try {
      const keyword = customer?.code || customer?.id;
      const community = getCommunityFromBranch(customer?.branch);
      const res = await apiGetConsumptionMonitoring({
        page: 1,
        size: 10,
        keyWord: keyword,
        community,
        period: dayjs().startOf('month').format('YYYY-MM-DD')
      });
      setCurrentMonthConsumption(res?.data?.data?.[0]?.final_consumption ?? null);
    } catch (e) {
      console.error(e);
    }
  };

  // ── Fetch Warnings from API ──
  const fetchWarnings = async (customerId, serialNo) => {
    try {
      setLoadingWarnings(true);
      // Construct fromDate and toDate to get active warnings (status 0)
      // Actually we just need active warnings, status=0, isWarning=1, keyWord = customerId or serialNo
      const res = await apiGetAllWarnings({
        page: 1,
        size: 100,
        status: 0,
        isWarning: 1,
        keyWord: customerId || serialNo
      });
      setApiWarnings(res?.data?.data || []);
    } catch (e) {
      console.error(e);
      setApiWarnings([]);
    } finally {
      setLoadingWarnings(false);
    }
  };

  // ── Fetch Yesterday's Consumption ──
  const fetchYesterdayConsumption = async (customerId) => {
    try {
      const dateStr = dayjs().subtract(1, 'day').format('YYYY-MM-DD');
      const res = await apiGetStatisticsCustomerByDayV2({
        page: 1,
        size: 10,
        customerId: customerId,
        fromDate: dateStr,
        toDate: dateStr
      });
      const val = res.data?.data?.[0]?.water_consumption_of_the_day;
      setYesterdayConsumption(val ?? 0);
    } catch (e) {
      console.error(e);
      setYesterdayConsumption(0);
    }
  };

  // ── Auto-load charts & warnings when device data is ready ──
  useEffect(() => {
    if (deviceData) {
      const custId = deviceData?.customer?.code || deviceData?.customer_id;

      // Fetch yesterday's consumption for quick stat
      fetchYesterdayConsumption(custId);

      fetchDayChart(selectedDate, custId);
      if (custId) fetchMonthChart(selectedMonth, custId);

      fetchPrevMonthsConsumption(deviceData?.customer);
      fetchCurrentMonthConsumption(deviceData?.customer);

      // Fetch warnings using customer code or serial_no
      const warningKeyword =
        deviceData?.customer?.code || deviceData?.customer_id || deviceData.serial_no;
      if (warningKeyword) {
        fetchWarnings(warningKeyword, deviceData.serial_no);
      }
    }
  }, [deviceData]);

  // ── Parse device sub-objects ──
  const _3_0 = useMemo(() => {
    try {
      return deviceData ? JSON.parse(deviceData?.['/3/0']) : {};
    } catch {
      return {};
    }
  }, [deviceData]);

  const _80_0 = useMemo(() => {
    try {
      return deviceData ? JSON.parse(deviceData?.['/80/0']) : {};
    } catch {
      return {};
    }
  }, [deviceData]);

  const _82_0 = useMemo(() => {
    try {
      return deviceData ? JSON.parse(deviceData?.['/82/0']) : {};
    } catch {
      return {};
    }
  }, [deviceData]);

  const _84_0 = useMemo(() => {
    try {
      return deviceData ? JSON.parse(deviceData?.['/84/0']) : {};
    } catch {
      return {};
    }
  }, [deviceData]);

  const _99_0 = useMemo(() => {
    try {
      return deviceData ? JSON.parse(deviceData?.['/99/0']) : {};
    } catch {
      return {};
    }
  }, [deviceData]);

  // ── Battery calc ──
  const remainingBatCap = useMemo(() => {
    /* Công thức cũ:
    const installTime = new Date(deviceData?.created_at).getTime();
    const lastSeenAt = deviceData?.last_seen_at;
    const timeDifference = lastSeenAt - installTime;
    const cap =
      ((8500 * 1000 -
        (timeDifference * 30) / (3600 * 1000) -
        deviceData?.number_of_updates * 1394.52) /
        (8500 * 1000)) *
      100;
    */

    const initialBattery = 8500 * 1000;
    const startedAt = new Date(deviceData?.install_time || deviceData?.created_at).getTime();
    const lastSeenAt = Number(deviceData?.last_seen_at);
    const operatingTimeMs =
      Number.isFinite(startedAt) && Number.isFinite(lastSeenAt)
        ? Math.max(0, lastSeenAt - startedAt)
        : 0;
    const numberOfUpdates = Math.max(0, Number(deviceData?.number_of_updates) || 0);

    const timeConsumption = (operatingTimeMs * 30) / (3600 * 1000);
    const transmissionConsumption = numberOfUpdates * 1394.52;
    const remainingBattery = initialBattery - timeConsumption - transmissionConsumption;
    const remainingPercentage = (remainingBattery / initialBattery) * 100;

    return Math.max(0, Math.min(100, remainingPercentage));
  }, [deviceData]);

  // ── Warnings ──
  const warnings = useMemo(() => {
    // Determine connection status based on last_seen_at and reportPeriod
    let is_online = false;
    const reportPeriod = _84_0?.reportPeriod || 86400;
    if (deviceData?.last_seen_at) {
      const nowSec = Math.floor(Date.now() / 1000);
      const lastSeenSec = Math.floor(deviceData.last_seen_at / 1000);
      if (nowSec - lastSeenSec <= reportPeriod + 60) {
        is_online = true;
      }
    }

    // Map API warnings
    const checkType = (type) => apiWarnings.some((w) => w.type === type);

    return {
      connectionLost: !is_online,
      manualRead: checkType('MANUAL_READING'), // Update types according to backend logic if necessary
      antiDemolition: checkType('ANTI_DEMOLITION'),
      magneticAttack: checkType('MAGNETIC_ATTACK'),
      leakage: checkType('LEAKAGE'),
      unusualConsumption: checkType('ABNORMAL_USAGE'),
      stuck: checkType('STUCK_METER'),
      reverseFlow: checkType('REVERSE_FLOW'),
      overflow: checkType('OVERFLOW')
    };
  }, [apiWarnings, deviceData, _84_0]);

  const warningCount = Object.values(warnings).filter(Boolean).length;

  // Update isOnline for the whole UI
  const isOnline = !warnings.connectionLost;

  // ── Chart series: by day ──
  const dayChartSeries = useMemo(() => {
    if (!dayData) return [{ name: 'Tiêu thụ giờ', data: [], type: 'bar' }];
    const hourly = dayData?.diffirenceOfTheFlowWaterMeter?.map((v) => (isNumber(v) ? v : 0)) || [];
    const startReading = dayData?.start_meter_reading_at_day || 0;
    const cumulative = [];
    let acc = startReading;
    for (let i = 0; i < hourly.length; i++) {
      cumulative.push(acc);
      acc += hourly[i];
    }
    return [
      { name: 'Tiêu thụ giờ (lít)', data: hourly, type: 'bar' },
      { name: 'Chỉ số đồng hồ (lít)', data: cumulative, type: 'line' }
    ];
  }, [dayData]);

  // ── Chart series: by month ──
  const monthChartSeries = useMemo(() => {
    if (!monthData?.length) return [{ name: 'Tiêu thụ ngày', data: [], type: 'bar' }];
    return [
      {
        name: 'Tiêu thụ ngày (lít)',
        data: monthData.map((item) =>
          isNumber(item?.water_consumption_of_the_day) ? item?.water_consumption_of_the_day : 0
        ),
        type: 'bar'
      }
    ];
  }, [monthData]);

  const monthChartLabels = useMemo(
    () => monthData.map((item) => item?.denseData_startCollectionTime ?? '—'),
    [monthData]
  );

  // ── Month options ──
  const generateMonthOptions = () => {
    const opts = [];
    const current = moment();
    for (let m = 0; m <= 11; m++) {
      const mm = moment().subtract(m, 'months');
      opts.push({ label: `Tháng ${mm.format('MM/YYYY')}`, value: mm.format('YYYY-MM') });
    }
    return opts;
  };

  const latestConsumption = Math.round(deviceData?.water_consumption_of_the_day ?? 0);
  const signal = ((_99_0?.rsrp ?? 0) * 0.1).toFixed(0);
  const signalValue = Number(signal);
  const signalColor = signalValue >= -100 ? '#10b981' : signalValue >= -110 ? '#f59e0b' : '#e11d48';
  const customer = deviceData?.customer;

  if (error) return <div style={{ padding: 24 }}>{error}</div>;

  return (
    <ContainerBody>
      {/* ── Breadcrumb + Back ── */}
      <div className="detail-top-bar">
        <Breadcrumb homePath={'/dashboard'} routeSegments={[{ name: 'Thông tin chi tiết' }]} />
        <Button
          onClick={() => navigate(-1)}
          icon={<ArrowLeftOutlined />}
          style={{ height: 38, paddingInline: 20 }}>
          Quay lại
        </Button>
      </div>

      {loading ? (
        <Skeleton active paragraph={{ rows: 18 }} />
      ) : (
        <>
          {/* ══════════════════════════════════════════════════
              HEADER – 4 quick-stat cards
          ══════════════════════════════════════════════════ */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Kết nối */}
            <Col xs={12} sm={6}>
              <Card className="detail-stat-card" bodyStyle={{ padding: '14px 18px' }}>
                <div className="stat-label">Kết nối</div>
                <div className="stat-value">
                  <span
                    className="status-dot"
                    style={{ background: isOnline ? '#10b981' : '#e11d48' }}
                  />
                  <span style={{ color: isOnline ? '#10b981' : '#e11d48' }}>
                    {isOnline ? 'Online' : 'Offline'}
                  </span>
                </div>
              </Card>
            </Col>

            {/* Tiêu thụ gần nhất */}
            <Col xs={12} sm={6}>
              <Card className="detail-stat-card" bodyStyle={{ padding: '14px 18px' }}>
                <div className="stat-label">
                  Tiêu thụ ngày {dayjs().subtract(1, 'day').format('DD/MM/YYYY')}
                </div>
                <div className="stat-value">
                  <ThunderboltOutlined style={{ color: '#0284c7', marginRight: 6 }} />
                  {yesterdayConsumption !== null
                    ? `${formatCurrency(Math.round(yesterdayConsumption))} L`
                    : 'Đang tải...'}
                </div>
              </Card>
            </Col>

            {/* Tín hiệu */}
            <Col xs={12} sm={6}>
              <Card className="detail-stat-card" bodyStyle={{ padding: '14px 18px' }}>
                <div className="stat-label">Tín hiệu</div>
                <div className="stat-value">
                  <SignalFilled style={{ color: signalColor, marginRight: 6 }} />
                  <span style={{ color: signalColor }}>{signal} dBm</span>
                </div>
              </Card>
            </Col>

            {/* Cảnh báo */}
            <Col xs={12} sm={6}>
              <Card className="detail-stat-card" bodyStyle={{ padding: '14px 18px' }}>
                <div className="stat-label">Cảnh báo</div>
                <div className="stat-value">
                  <WarningOutlined
                    style={{ color: warningCount > 0 ? '#ea580c' : '#10b981', marginRight: 6 }}
                  />
                  {warningCount} bất thường
                </div>
              </Card>
            </Col>
          </Row>

          {/* ══════════════════════════════════════════════════
              ROW 1 – Thông tin khách hàng | ĐH | Thiết bị
          ══════════════════════════════════════════════════ */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Thông tin khách hàng */}
            <Col xs={24} md={8}>
              <SectionCard icon={<UserOutlined />} title="Thông tin khách hàng" color="#0284c7">
                <InfoRow label="Tên khách hàng" value={customer?.name} />
                <InfoRow label="Chi nhánh" value={customer?.branch} />
                <InfoRow label="Tổ quản lý" value={customer?.manage_group} />
                <InfoRow label="Đồng hồ khối" value={customer?.parent_device} />
                <InfoRow label="Tuyến" value={customer?.route} />
                <InfoRow label="DBĐD" value={customer?.code} />
                <InfoRow label="Địa chỉ" value={customer?.address} />
                {/* <InfoRow
                  label="Tọa độ"
                  value={
                    deviceData?.latitude && deviceData?.longitude
                      ? `${deviceData.latitude}, ${deviceData.longitude}`
                      : 'Chưa xác định'
                  }
                /> */}
              </SectionCard>
            </Col>

            {/* Thông tin đồng hồ */}
            <Col xs={24} md={8}>
              <SectionCard icon={<DashboardOutlined />} title="Thông tin đồng hồ" color="#0284c7">
                <InfoRow label="Sêri đồng hồ" value={customer?.serial_number} />
                <InfoRow label="Tên đồng hồ" value={customer?.device_name} />
                <InfoRow label="Cỡ đồng hồ" value={customer?.size} />
                <InfoRow label="Loại đồng hồ" value={customer?.type} />
                <InfoRow
                  label="Ngày lắp"
                  value={customer?.install_time ? customer?.install_time : '—'}
                />
                <InfoRow
                  label="Ngày kiểm định"
                  value={customer?.inspection_date ? customer?.inspection_date : '—'}
                />
                <InfoRow label="Số năm hiệu lực" value={customer?.year_valid} />
                <InfoRow label="Chỉ số tối đa (lít)" value={_80_0?.maxReading} />
              </SectionCard>
            </Col>

            {/* Thông tin thiết bị */}
            <Col xs={24} md={8}>
              <SectionCard icon={<ToolOutlined />} title="Thông tin thiết bị" color="#0284c7">
                <InfoRow label="Model" value={deviceData?.model} />
                <InfoRow label="Số Seri" value={deviceData?.serial_no} />
                <InfoRow label="Số IMEI" value={deviceData?.imei} />
                <InfoRow
                  label="Thời điểm lắp đặt"
                  value={
                    deviceData?.install_time
                      ? moment(deviceData.install_time).format('DD/MM/YYYY HH:mm:ss')
                      : moment(deviceData?.created_at).format('DD/MM/YYYY HH:mm:ss')
                  }
                />
                <InfoRow
                  label="Hệ số lít/xung"
                  value={convertPN?.find((item) => item?.value === _80_0?.pn)?.label}
                />
                <InfoRow
                  label="Điện áp pin (V)"
                  value={(_3_0?.powerSourceVoltage / 100)?.toFixed(2)}
                />
                <InfoRow label="Lượng pin còn lại (%)" value={remainingBatCap?.toFixed(0)} />
                <InfoRow
                  label="Chu kỳ gửi báo cáo (phút)"
                  value={_84_0?.reportPeriod ? _84_0.reportPeriod / 60 : '—'}
                />
                <InfoRow label="Chu kỳ ghi dữ liệu dày (giờ)" value={_84_0?.reportingInterval} />
              </SectionCard>
            </Col>
          </Row>

          {/* ══════════════════════════════════════════════════
              ROW 2 – Đo lường | Cảnh báo | Truyền dữ liệu
          ══════════════════════════════════════════════════ */}
          <Row gutter={[16, 16]} style={{ marginBottom: 16 }}>
            {/* Thông tin đo lường */}
            <Col xs={24} md={8}>
              <SectionCard
                icon={<DashboardOutlined style={{ color: '#0284c7' }} />}
                title="Thông tin đo lường"
                color="#0284c7">
                <InfoRow label="Chỉ số đồng hồ (lít)" value={formatCurrency(_80_0?.meterReading)} />
                <InfoRow
                  label="Lượng nước tiêu thụ trong ngày (lít)"
                  value={
                    yesterdayConsumption !== null ? Math.round(yesterdayConsumption) : 'Đang tải...'
                  }
                />
                <InfoRow
                  label="Lượng nước chảy ngược trong ngày (lít)"
                  value={Math.round(deviceData?.water_reverse_of_the_day ?? 0)}
                />
                <InfoRow
                  label={
                    'Lượng nước tiêu thụ từ đầu tháng ' +
                    dayjs().startOf('month').format('M') +
                    ' đến hiện tại (m3)'
                  }
                  value={
                    currentMonthConsumption != null
                      ? formatCurrency(currentMonthConsumption, 3)
                      : 'Đang tải...'
                  }
                />
                {loadingPrevMonths ? (
                  <div className="detail-info-row">
                    <span className="detail-info-label">Đang tải...</span>
                  </div>
                ) : (
                  prevMonthsConsumption.map((item, idx) => (
                    <InfoRow
                      key={idx}
                      label={`Lượng nước tiêu thụ tháng ${item.month.format('M')} (m3)`}
                      value={
                        item.finalConsumption != null
                          ? formatCurrency(item.finalConsumption, 3)
                          : '—'
                      }
                    />
                  ))
                )}
                <InfoRow
                  label="Thời điểm ghi dữ liệu dày đặc"
                  value={deviceData?.dense_data_start_time ?? '00:00:00'}
                />
              </SectionCard>
            </Col>

            {/* Thông tin cảnh báo */}
            <Col xs={24} md={8}>
              <SectionCard
                icon={<AlertOutlined style={{ color: '#ea580c' }} />}
                title="Thông tin cảnh báo"
                color="#c26e00">
                <div className="warning-row">
                  <span className="detail-info-label">Mất kết nối</span>
                  <WarningBadge active={warnings.connectionLost} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Cần đọc chỉ số thủ công</span>
                  <WarningBadge active={warnings.manualRead} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Tấn công tháo rời</span>
                  <WarningBadge active={warnings.antiDemolition} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Tấn công từ tính</span>
                  <WarningBadge active={warnings.magneticAttack} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Rò rỉ nước</span>
                  <WarningBadge active={warnings.leakage} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Tiêu thụ bất thường</span>
                  <WarningBadge active={warnings.unusualConsumption} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Kẹt công tơ</span>
                  <WarningBadge active={warnings.stuck} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Dòng chảy ngược</span>
                  <WarningBadge active={warnings.reverseFlow} />
                </div>
                <div className="warning-row">
                  <span className="detail-info-label">Dòng chảy vượt ngưỡng</span>
                  <WarningBadge active={warnings.overflow} />
                </div>
              </SectionCard>
            </Col>

            {/* Thông tin truyền dữ liệu */}
            <Col xs={24} md={8}>
              <SectionCard
                icon={<ApiOutlined style={{ color: '#7c3aed' }} />}
                title="Thông tin truyền dữ liệu"
                color="#531dab">
                <InfoRow
                  label="Thời điểm nhận báo cáo"
                  value={
                    deviceData?.last_seen_at
                      ? new Intl.DateTimeFormat('vi-VN', {
                          year: 'numeric',
                          month: '2-digit',
                          day: '2-digit',
                          hour: '2-digit',
                          minute: '2-digit',
                          second: '2-digit'
                        }).format(deviceData.last_seen_at)
                      : '—'
                  }
                />
                <InfoRow label="Cường độ sóng (dBm)" value={signal} />
                <InfoRow label="Số IMSI" value={deviceData?.imsi} />
                <InfoRow label="Số ICCID SIM" value={deviceData?.iccid} />
                <InfoRow label="Số máy" value={_99_0?.ip} />
                <InfoRow
                  label="Phương thức truyền"
                  value={deviceData?.connection_type ?? 'NB-IoT'}
                />
                <InfoRow label="Nhà mạng" value={deviceData?.operator ?? '—'} />
              </SectionCard>
            </Col>
          </Row>

          {/* ══════════════════════════════════════════════════
              CHARTS – Tiêu thụ theo ngày / theo tháng
          ══════════════════════════════════════════════════ */}
          <Card bodyStyle={{ padding: 0 }} style={{ marginBottom: 16 }}>
            <Tabs
              defaultActiveKey="day"
              tabBarStyle={{ padding: '0 16px', marginBottom: 0 }}
              tabBarExtraContent={null}>
              {/* ── Tab: Theo ngày ── */}
              <TabPane tab="📊 Tiêu thụ theo ngày" key="day">
                <div style={{ padding: '12px 16px 16px' }}>
                  <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                      <Typography.Text strong style={{ marginRight: 8 }}>
                        Chọn ngày:
                      </Typography.Text>
                      <DatePicker
                        value={selectedDate}
                        format="DD/MM/YYYY"
                        style={{ height: 34 }}
                        allowClear={false}
                        onChange={(date) => {
                          if (!date) return;
                          setSelectedDate(date);
                          const custId = deviceData?.customer?.code || deviceData?.customer_id;
                          fetchDayChart(date, custId);
                        }}
                      />
                    </Col>
                    {dayData && (
                      <Col>
                        <Tag color="blue">
                          Tiêu thụ:{' '}
                          <strong>
                            {formatCurrency(Math.round(dayData?.water_consumption_of_the_day ?? 0))}{' '}
                            lít
                          </strong>
                        </Tag>
                      </Col>
                    )}
                  </Row>

                  {loadingDay ? (
                    <Loading />
                  ) : dayData ? (
                    <Chart
                      options={{
                        colors: [...COLORS, '#666666'],
                        chart: { zoom: { enabled: false }, toolbar: { show: true } },
                        plotOptions: {
                          bar: { horizontal: false, columnWidth: '60%', borderRadius: 2 }
                        },
                        dataLabels: { enabled: false },
                        stroke: {
                          show: true,
                          width: [2, 3],
                          colors: ['transparent', '#666666']
                        },
                        markers: {
                          size: [0, 4],
                          colors: ['#fff', '#666666'],
                          strokeColors: ['#fff', '#666666'],
                          strokeWidth: 2
                        },
                        labels: Array.from(
                          { length: 24 },
                          (_, i) => String(i).padStart(2, '0') + ':00'
                        ),
                        yaxis: [
                          { show: true, title: { text: 'Lượng nước (lít)' } },
                          {
                            opposite: true,
                            show: true,
                            title: { text: 'Chỉ số đồng hồ (lít)' }
                          }
                        ],
                        grid: { show: true },
                        tooltip: {
                          y: { formatter: (val) => `${formatCurrency(val)} lít` }
                        },
                        legend: { horizontalAlign: 'left' }
                      }}
                      type="bar"
                      series={dayChartSeries}
                      height={360}
                    />
                  ) : (
                    <Empty description="Không có dữ liệu tiêu thụ trong ngày này" />
                  )}
                </div>
              </TabPane>

              {/* ── Tab: Theo tháng ── */}
              <TabPane tab="📈 Tiêu thụ theo tháng" key="month">
                <div style={{ padding: '12px 16px 16px' }}>
                  <Row gutter={[8, 8]} align="middle" style={{ marginBottom: 12 }}>
                    <Col>
                      <Typography.Text strong style={{ marginRight: 8 }}>
                        Chọn tháng:
                      </Typography.Text>
                      <Select
                        style={{ width: 180, height: 34 }}
                        value={selectedMonth}
                        options={generateMonthOptions()}
                        onChange={(val) => {
                          setSelectedMonth(val);
                          const custId = deviceData?.customer?.code || deviceData?.customer_id;
                          if (custId) fetchMonthChart(val, custId);
                        }}
                      />
                    </Col>
                  </Row>

                  {loadingMonth ? (
                    <Loading />
                  ) : monthData?.length ? (
                    <Chart
                      options={{
                        colors: COLORS,
                        chart: { zoom: { enabled: false }, toolbar: { show: true } },
                        plotOptions: {
                          bar: { horizontal: false, columnWidth: '60%', borderRadius: 2 }
                        },
                        dataLabels: { enabled: false },
                        labels: monthChartLabels,
                        yaxis: [{ show: true, title: { text: 'Lượng nước (lít)' } }],
                        grid: { show: true },
                        tooltip: {
                          y: { formatter: (val) => `${formatCurrency(val)} lít` }
                        },
                        legend: { horizontalAlign: 'left' }
                      }}
                      type="bar"
                      series={monthChartSeries}
                      height={360}
                    />
                  ) : (
                    <Empty description="Không có dữ liệu tiêu thụ trong tháng này" />
                  )}
                </div>
              </TabPane>
            </Tabs>
          </Card>
        </>
      )}
    </ContainerBody>
  );
};

export default DeviceInformationDetail;
